/**
 * SEO HEADING STRUCTURE - Online Workspace
 * ========================================
 * 
 * TITLE: Online Workspace | CSW | Online Workspace | Productivity Tool | Study Stream
 * META DESCRIPTION: Free online workspace with customizable backgrounds...
 * 
 * H1 (1 tag):
 *   - "Online Workspace" (hero section)
 * 
 * H2 (3 tags):
 *   - "Everything You Need to Stay Productive" (features section)
 *   - "Why Choose Our Workspace?" (benefits section)
 *   - "Ready to Transform Your Productivity?" (CTA section)
 * 
 * H3 (10 tags):
 *   - Feature card titles (6 cards): "Customize Your Background", "Flexible Modes", etc.
 *   - Benefit titles (4 items): "Zero installation", "Real-time presence", etc.
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Monitor, Users, Cloud, Zap, CheckCircle2, Globe, ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const workspaceAlternatives = [
  { name: "Notion", path: "/online-workspace/csw-vs-notion/" },
  { name: "Monday.com", path: "/online-workspace/csw-vs-monday/" },
  { name: "ClickUp", path: "/online-workspace/csw-vs-clickup/" },
  { name: "Asana", path: "/online-workspace/csw-vs-asana/" },
  { name: "Trello", path: "/online-workspace/csw-vs-trello/" },
  { name: "Slack", path: "/online-workspace/csw-vs-slack/" },
  { name: "Miro", path: "/online-workspace/csw-vs-miro/" },
  { name: "Basecamp", path: "/online-workspace/csw-vs-basecamp/" },
  { name: "Coda", path: "/online-workspace/csw-vs-coda/" },
  { name: "Airtable", path: "/online-workspace/csw-vs-airtable/" },
];

const features = [
  {
    icon: Monitor,
    title: "Customize Your Background",
    description: "Personalize your workspace with custom backgrounds that match your mood and enhance your focus.",
  },
  {
    icon: Users,
    title: "Flexible Modes",
    description: "Choose your mode - study online with people, or create a private space with music and timer.",
  },
  {
    icon: Zap,
    title: "Track Your Productivity",
    description: "Monitor your progress and see how much you've accomplished with real-time productivity insights.",
  },
  {
    icon: CheckCircle2,
    title: "Kanban Task Management",
    description: "Organize your tasks with an intuitive Kanban board. Drag, drop, and track your work.",
  },
  {
    icon: Cloud,
    title: "Calendar Reminders",
    description: "Never miss important deadlines. Set reminders and sync with your calendar.",
  },
  {
    icon: Globe,
    title: "Meet Like-Minded People",
    description: "Connect with others in Chill, Study, or Work modes. Find friends who share your focus.",
  },
];

export default function OnlineWorkspace() {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Online Workspace | CSW | Online Workspace | Productivity Tool | Study Stream"
        description="Free online workspace with customizable backgrounds, Kanban task management, Pomodoro timer, and productivity tracking. Study with focus and stay organized."
        keywords="online workspace, virtual workspace, study room online, productivity workspace, task management, pomodoro timer"
        canonicalUrl="/online-workspace/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
          
          {/* Animated Glow Effects */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                  <span className="text-sm font-medium text-muted-foreground">Work From Anywhere</span>
                </div>
                
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gradient">Online Workspace</span>
                </h1>
                
                <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                  A virtual productivity environment designed for focus, collaboration, and achievement.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Start Working Now
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Try for Free
                    </a>
                  </Button>
                </div>
                
                <div className="flex items-center gap-8 pt-8">
                  <div>
                    <p className="text-3xl font-bold text-gradient">24/7</p>
                    <p className="text-sm text-muted-foreground">Always Open</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">3 Modes</p>
                    <p className="text-sm text-muted-foreground">Chill, Study, Work</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Free</p>
                    <p className="text-sm text-muted-foreground">Forever</p>
                  </div>
                </div>
              </div>
              
              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video 
                    src="/videos/productivity-demo.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
                
                {/* Floating Card */}
                <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Your Virtual Office</p>
                      <p className="text-xl font-bold text-foreground">Work Anywhere</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Monitor className="w-6 h-6 text-primary-foreground" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Everything You Need to{" "}
                <span className="text-gradient">Stay Productive</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                A complete digital workspace with all the tools for focused, productive work
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl"
                  >
                    <CardContent className="p-8 space-y-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      
                      <h3 className="text-xl font-semibold text-foreground font-display">
                        {feature.title}
                      </h3>
                      
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 px-4 relative">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
          
          <div className="container mx-auto max-w-4xl relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Why Choose Our{" "}
                <span className="text-gradient">Workspace</span>?
              </h2>
              <p className="text-xl text-muted-foreground">
                Built for modern workers who value flexibility, focus, and connection
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {[
                { title: "Zero installation", desc: "Works in any browser with no downloads or setup required." },
                { title: "Real-time presence", desc: "See who's working alongside you with live presence indicators." },
                { title: "Distraction-free", desc: "A clean, minimal environment designed to help you focus." },
                { title: "Free forever", desc: "Full access to all features with no limitations or hidden costs." },
              ].map((item, index) => (
                <div 
                  key={index}
                  className="p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg"
                >
                  <h3 className="text-xl font-semibold text-foreground mb-3 font-display">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
              Ready to Transform Your{" "}
              <span className="text-gradient">Productivity</span>?
            </h2>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Join thousands of professionals, students, and creators who work better together.
            </p>
            <Button 
              size="lg" 
              className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Launch Your Workspace
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </section>

        {/* Comprehensive Content Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background to-muted/50" />
          
          <div className="container mx-auto max-w-4xl relative z-10">
            <div className="prose prose-lg max-w-none">
              {/* Introduction */}
              <div className="mb-16">
                <h2 className="text-3xl md:text-4xl font-bold font-display mb-6 text-foreground">
                  Your All-In-One Online Workspace — Smarter, Faster, More Human
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                  <strong className="text-foreground">Built for focus. Designed for collaboration. Powered by AI.</strong>
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  CSW.live is a free, unlimited online workspace built for how people actually work and study today. It combines <Link to="/study-timer/" className="text-primary hover:underline">focus tools</Link>, <Link to="/task-management/" className="text-primary hover:underline">task management</Link>, collaboration, and automation into one clean, cloud-based platform — so you spend less time organizing work and more time actually doing it.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Whether you're a student, freelancer, or part of a remote team, CSW adapts to your workflow instead of forcing you into one. Research from{" "}
                  <a href="https://hbr.org/2020/07/remote-work-isnt-a-perk-to-negotiate-its-how-we-work-now" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Harvard Business Review</a>{" "}
                  shows that flexible work environments significantly improve productivity and job satisfaction.
                </p>
              </div>

              {/* AI & Automation */}
              <div className="mb-16 p-8 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50">
                <h2 className="text-2xl md:text-3xl font-bold font-display mb-4 text-foreground">
                  A Smarter Online Workspace with AI & Automation
                </h2>
                <p className="text-lg text-muted-foreground mb-4">
                  <strong className="text-foreground">Let AI handle the busywork. You stay in flow.</strong>
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Modern workspaces shouldn't just store your work — they should actively help you work better. CSW integrates AI and automation to reduce mental load and keep momentum high. According to{" "}
                  <a href="https://www.mckinsey.com/capabilities/quantumblack/our-insights/the-state-of-ai" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">McKinsey's research on AI in the workplace</a>, AI-powered tools can boost productivity by up to 40% for knowledge workers.
                </p>
                <p className="text-muted-foreground mb-4"><strong className="text-foreground">AI-powered capabilities include:</strong></p>
                <ul className="space-y-2 text-muted-foreground mb-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Smart task prioritization and focus suggestions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>AI search answers across tasks, files, and rooms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Thread summaries and channel recaps (coming soon)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>AI-generated insights from real-time workspace data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>No-code automation for recurring workflows</span>
                  </li>
                </ul>
                <p className="text-muted-foreground">
                  Think of CSW as a quiet digital assistant — not noisy bots, just intelligent support that works in the background.
                </p>
              </div>

              {/* Collaboration */}
              <div className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold font-display mb-4 text-foreground">
                  Collaboration That Feels Natural, Not Forced
                </h2>
                <p className="text-lg text-muted-foreground mb-4">
                  <strong className="text-foreground">Built for real teamwork — not endless meetings.</strong>
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  CSW is a true cloud-based workspace designed for real-time collaboration without fragmentation. Whether you prefer <Link to="/study-with-me/" className="text-primary hover:underline">studying with others</Link> or <Link to="/study-with-friends/" className="text-primary hover:underline">working with friends</Link>, our platform supports natural connection. Studies from{" "}
                  <a href="https://journals.sagepub.com/doi/10.1177/0018726718773384" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">SAGE Journals</a>{" "}
                  confirm that collaborative work environments enhance creativity and problem-solving.
                </p>
                <p className="text-muted-foreground mb-4"><strong className="text-foreground">Collaboration features include:</strong></p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Built-in team chat with threads and mentions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Shared documents with live editing and comments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Shared calendars and scheduling tools</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Audio and video conferencing inside your workspace</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Multi-format support for notes, files, and tasks</span>
                  </li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  Everything lives in one place — no jumping between chat apps, file tools, and project trackers.
                </p>
              </div>

              {/* Powerful Features */}
              <div className="mb-16 p-8 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50">
                <h2 className="text-2xl md:text-3xl font-bold font-display mb-4 text-foreground">
                  Powerful Features Without the Complexity
                </h2>
                <p className="text-lg text-muted-foreground mb-4">
                  <strong className="text-foreground">Everything you expect from a modern online workspace.</strong>
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  CSW includes the core features teams and students rely on — without overwhelming dashboards or bloated menus. Explore our <Link to="/flash-cards/" className="text-primary hover:underline">flashcard tools</Link> and <Link to="/quizzes/" className="text-primary hover:underline">quiz features</Link> for enhanced learning.
                </p>
                <p className="text-muted-foreground mb-4"><strong className="text-foreground">Key features:</strong></p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Kanban boards and task management tools</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Customizable dashboards and workflows</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Progress trackers and visual timelines</span>
                    </li>
                  </ul>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Calendar integrations and reminders</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Cloud-based file storage with version control</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Shared document processing and comments</span>
                    </li>
                  </ul>
                </div>
                <p className="text-muted-foreground mt-4">
                  Whether you prefer simple task lists or structured project workflows, CSW scales with you.
                </p>
              </div>

              {/* Security */}
              <div className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold font-display mb-4 text-foreground">
                  Secure by Design
                </h2>
                <p className="text-lg text-muted-foreground mb-4">
                  <strong className="text-foreground">Your workspace. Your data. Always protected.</strong>
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Security and access control are built into every CSW workspace. Following best practices outlined by{" "}
                  <a href="https://www.nist.gov/cyberframework" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">NIST Cybersecurity Framework</a>, we ensure your sensitive data stays safe while collaboration remains seamless.
                </p>
                <p className="text-muted-foreground mb-4"><strong className="text-foreground">Security features include:</strong></p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Custom access controls and permissions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Secure file sharing with editing rights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Private and public workspace channels</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Version history and document recovery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Authorized user management for teams</span>
                  </li>
                </ul>
              </div>

              {/* Customization */}
              <div className="mb-16 p-8 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50">
                <h2 className="text-2xl md:text-3xl font-bold font-display mb-4 text-foreground">
                  Set Up Your Workspace Your Way
                </h2>
                <p className="text-lg text-muted-foreground mb-4">
                  <strong className="text-foreground">Customizable from day one.</strong>
                </p>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  CSW lets you shape your workspace around your needs. Whether you're setting up a <Link to="/study-room/" className="text-primary hover:underline">virtual study room</Link> or a <Link to="/group-study/" className="text-primary hover:underline">group study session</Link>, flexibility is built in.
                </p>
                <p className="text-muted-foreground mb-4"><strong className="text-foreground">Customization options:</strong></p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Project-based workspaces</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Public or private channels</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Workflow templates for common routines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Knowledge management spaces</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Flexible stakeholder and client access</span>
                  </li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  No rigid structures. No forced methodologies. Just a workspace that fits how you think.
                </p>
              </div>

              {/* Use Cases */}
              <div className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold font-display mb-4 text-foreground">
                  One Platform, Endless Use Cases
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  CSW is designed to support a wide range of workflows. Research from{" "}
                  <a href="https://www.educause.edu/research-and-publications" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">EDUCAUSE</a>{" "}
                  highlights how digital workspaces transform both academic and professional environments.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-card/30 border border-border/30">
                    <p className="font-semibold text-foreground mb-2">Students</p>
                    <p className="text-sm text-muted-foreground">Group projects, exam prep, <Link to="/study-stream/" className="text-primary hover:underline">study streams</Link></p>
                  </div>
                  <div className="p-4 rounded-xl bg-card/30 border border-border/30">
                    <p className="font-semibold text-foreground mb-2">Remote Teams</p>
                    <p className="text-sm text-muted-foreground">Sprint planning, Kanban boards, roadmaps</p>
                  </div>
                  <div className="p-4 rounded-xl bg-card/30 border border-border/30">
                    <p className="font-semibold text-foreground mb-2">Freelancers</p>
                    <p className="text-sm text-muted-foreground">Client work, task tracking, focus sessions</p>
                  </div>
                  <div className="p-4 rounded-xl bg-card/30 border border-border/30">
                    <p className="font-semibold text-foreground mb-2">Product Teams</p>
                    <p className="text-sm text-muted-foreground">Features, epics, documentation</p>
                  </div>
                  <div className="p-4 rounded-xl bg-card/30 border border-border/30 sm:col-span-2">
                    <p className="font-semibold text-foreground mb-2">Creators & Marketers</p>
                    <p className="text-sm text-muted-foreground">Campaigns, content, collaboration</p>
                  </div>
                </div>
                <p className="text-muted-foreground mt-4">
                  If your work happens online, CSW keeps it organized and connected.
                </p>
              </div>

              {/* How CSW Compares */}
              <div className="mb-16 p-8 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50">
                <h2 className="text-2xl md:text-3xl font-bold font-display mb-4 text-foreground">
                  How CSW Compares to Other Online Workspaces
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Many online workspace tools focus on either collaboration or project management — rarely both, and almost never focus. Learn more about productivity science from{" "}
                  <a href="https://www.apa.org/topics/healthy-workplaces" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">American Psychological Association</a>.
                </p>
                <p className="text-muted-foreground mb-4"><strong className="text-foreground">CSW stands out because it:</strong></p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Combines focus tools + collaboration + workflow management</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Supports Kanban, dashboards, and roadmaps without heavy setup</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Avoids bloated reports and over-engineered Scrum systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Integrates AI without overwhelming users</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Works equally well for individuals and teams</span>
                  </li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  Recommended for anyone who values clarity, momentum, and focus. Check out our <Link to="/blog/top-10-digital-tools-for-academic-success/" className="text-primary hover:underline">top digital tools for academic success</Link>.
                </p>
              </div>

              {/* Final CTA */}
              <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 border border-border/50">
                <h2 className="text-2xl md:text-3xl font-bold font-display mb-4 text-foreground">
                  The Online Workspace That Actually Works
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  CSW.live is completely free — no ads, no limits, no locked features.<br />
                  Plan, collaborate, focus, and build — all in one workspace.
                </p>
                <p className="text-muted-foreground mb-6">
                  Join thousands of students and professionals already using CSW to work better.
                </p>
                <Button 
                  size="lg" 
                  className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                  asChild
                >
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Start Using CSW.live — It's Free
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Compare Workspace Tools Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Compare{" "}
                <span className="text-gradient">Workspace Tools</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                See how CSW compares to other popular productivity platforms
              </p>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {workspaceAlternatives.map((tool) => (
                <Link
                  key={tool.path}
                  to={tool.path}
                  className="group p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-lg text-center"
                >
                  <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                    CSW vs {tool.name}
                  </span>
                  <ExternalLink className="w-3 h-3 inline-block ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}